import { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { useAppStore } from '@forwardflow/store/appStore'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { UserService } from '@forwardflow/services/UserService'
import { useGetUserCalenderInfo } from '@forwardflow/hooks/useGetUserCalenderInfo'
import { Screen } from '../frame/SetupFrame'
import { useStepFooter } from '../../hooks/useStepFooter'
import { Icon } from '../ui/Icon'
import { prettyPhone } from '../../utils/format'
import { ownerOf } from '../../utils/seats'
import {
   FF_ASSISTANT_NUMBER,
   FF_ASSISTANT_NUMBER_PRETTY,
} from '../../utils/constants'

const SENT_KEY = 'ff-onboarding:card-sent'

/** A vCard for the Forward Flow number, so "Save as a contact" works on any phone. */
const vcardUrl = () =>
   `data:text/vcard;charset=utf-8,${encodeURIComponent(['BEGIN:VCARD', 'VERSION:3.0', 'FN:Forward Flow', 'ORG:Forward Flow', `TEL;TYPE=CELL:${FF_ASSISTANT_NUMBER}`, 'NOTE:Live lead transfers and help by text.', 'END:VCARD'].join('\n'))}`

/**
 * Step 8: the Forward Flow number, and what it does.
 *
 * Texted to the customer's mobile on arrival (once per session), shown here
 * with the three things to know: live transfers come from it, we book an
 * appointment when they cannot pick up, and texting it gets help.
 */
export const NumberStep = () => {
   const { currentOrganization } = useAppStore()
   const { onboardingData, getNextStep } = useOnboardingStoreV2()
   const owner = ownerOf(onboardingData.invitedUsers)
   const ownerPhone = owner?.phone ? prettyPhone(owner.phone) : 'your mobile'
   const { data: calendarInfo } = useGetUserCalenderInfo(
      Boolean(currentOrganization)
   )
   const provider = calendarInfo?.email
      ? calendarInfo.provider === 'microsoft'
         ? 'Outlook'
         : 'Google'
      : onboardingData.isCalendarConnected
        ? 'your'
        : null

   const [sent, setSent] = useState<'idle' | 'sending' | 'sent' | 'failed'>(
      'idle'
   )
   const sendCard = async () => {
      setSent('sending')
      try {
         /* The number confirmed on the "You" step; the account record may not carry one yet. */
         await UserService.sendCard(owner?.phone)
         setSent('sent')
         try {
            sessionStorage.setItem(SENT_KEY, currentOrganization || '1')
         } catch {
            // storage unavailable
         }
      } catch {
         setSent('failed')
      }
   }
   // Send once per session on arrival, so the screen can say "we texted you".
   const autoSent = useRef(false)
   useEffect(() => {
      if (autoSent.current) return
      autoSent.current = true
      let already = false
      try {
         already =
            sessionStorage.getItem(SENT_KEY) === (currentOrganization || '1')
      } catch {
         // storage unavailable
      }
      if (already) setSent('sent')
      else void sendCard()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useStepFooter('Saved, continue', () => getNextStep(), {
      hint: 'Next: which leads we should leave alone.',
      testId: 'continue-number',
   })

   const sentLine =
      sent === 'sent'
         ? `texted to ${ownerPhone}`
         : sent === 'sending'
           ? 'sending the text'
           : sent === 'failed'
             ? 'the text did not go through'
             : ''
   const tiles = [
      {
         icon: 'phone',
         tone: 'purple',
         title: 'Live transfers',
         body: `A lead wants to talk? We call ${ownerPhone} and connect you.`,
      },
      {
         icon: 'calendar',
         tone: 'green',
         title: 'Can’t pick up?',
         body: provider
            ? `We book the appointment on ${provider === 'your' ? 'your calendar' : `your ${provider} Calendar`}.`
            : 'We book the appointment into Follow Up Boss.',
      },
      {
         icon: 'message',
         tone: 'warn',
         title: 'Text it for help',
         body: 'How a campaign is doing, what a lead said, anything in your data.',
      },
   ] as const

   return (
      <Screen
         stepKey="your-number"
         kind="number"
         title="We texted you a number. Save it."
         lede={`It was sent to ${ownerPhone}. Save it as “Forward Flow” so you recognise the call when a lead is on the line.`}
         width="md"
      >
         <div
            className="rounded-[20px] border border-[var(--ob-border)] bg-[var(--ob-surface)] p-4"
            data-testid="number-card"
         >
            <div className="flex items-center gap-4">
               <span className="flex shrink-0 items-center justify-center w-12 h-12 rounded-full bg-[var(--ob-primary)] text-white">
                  <Icon name="phone" size={20} />
               </span>
               <div className="flex-1 min-w-0">
                  <p
                     className="text-[26px] leading-none font-semibold tracking-tight whitespace-nowrap"
                     data-testid="ff-number"
                  >
                     {FF_ASSISTANT_NUMBER_PRETTY}
                  </p>
                  <p
                     className="mt-1.5 text-[13px] text-[var(--ob-text-2)] truncate"
                     data-testid="sent-line"
                  >
                     Forward Flow{sentLine ? ` · ${sentLine}` : ''}
                  </p>
               </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
               <a
                  href={vcardUrl()}
                  download="Forward Flow.vcf"
                  className="inline-flex items-center justify-center gap-2 h-10 rounded-full bg-[var(--ob-surface-2)] text-[13px] font-semibold hover:bg-[var(--ob-surface-3)]"
                  data-testid="save-contact"
               >
                  <Icon name="download" size={14} /> Save as a contact
               </a>
               <button
                  type="button"
                  onClick={() => void sendCard()}
                  disabled={sent === 'sending'}
                  className="inline-flex items-center justify-center gap-2 h-10 rounded-full bg-[var(--ob-surface-2)] text-[13px] font-semibold hover:bg-[var(--ob-surface-3)] disabled:opacity-60"
                  data-testid="text-again"
               >
                  <Icon name="message" size={14} />{' '}
                  {sent === 'failed' ? 'Try again' : 'Text it again'}
               </button>
            </div>
         </div>
         <div
            className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3"
            data-testid="number-explainer"
         >
            {tiles.map((t) => (
               <div
                  key={t.title}
                  className="rounded-2xl border border-[var(--ob-border)] bg-[var(--ob-surface)] p-3.5"
               >
                  <span
                     className={cn(
                        'inline-flex items-center justify-center w-8 h-8 rounded-full',
                        t.tone === 'purple' &&
                           'bg-[var(--ob-primary-tint)] text-[var(--ob-light)]',
                        t.tone === 'green' &&
                           'bg-[var(--ob-good-tint)] text-[var(--ob-good)]',
                        t.tone === 'warn' &&
                           'bg-[var(--ob-warn-tint)] text-[var(--ob-warn)]'
                     )}
                  >
                     <Icon name={t.icon} size={14} />
                  </span>
                  <p className="mt-2 text-[14px] font-semibold">{t.title}</p>
                  <p className="mt-0.5 text-[12px] leading-4 text-[var(--ob-text-2)]">
                     {t.body}
                  </p>
               </div>
            ))}
         </div>
      </Screen>
   )
}
